import { useEffect, useState } from "react";
import { ShieldCheck, Loader2, AlertCircle } from "lucide-react";
import { api } from "../App";
import { Button } from "./ui/button";
import { toast } from "sonner";

/* IdentityVerificationCard
 * ------------------------
 * Drop-in card for the Security page. Lets a user start (or re-start) Stripe
 * Identity verification — uploads a government ID + liveness selfie, all on
 * Stripe's hosted page. We use the redirect flow (no @stripe/stripe-js
 * dependency) so this works on iOS Safari without any extra setup.
 *
 * Webhook handler in the backend sets `identity_verified=true` on the user
 * doc when Stripe returns a "verified" event, so polling /identity/status
 * eventually flips the badge to green.
 */
export default function IdentityVerificationCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [launching, setLaunching] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/stripe/identity/status");
      setStatus(data);
    } catch (_e) {
      /* keep silent — section just won't load */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/stripe/identity/status")
      .then(({ data }) => { if (!cancelled) setStatus(data); })
      .catch(() => { /* silent */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    load();
    const search = new URLSearchParams(window.location.search);
    if (search.get("stripe_identity") === "done") {
      const t = setTimeout(load, 2500);
      return () => { cancelled = true; clearTimeout(t); };
    }
    return () => { cancelled = true; };
  }, []);

  const handleStart = async () => {
    setLaunching(true);
    try {
      const { data } = await api.post("/stripe/identity/session");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos iniciar la verificación");
    } finally {
      setLaunching(false);
    }
  };

  const verified = status?.identity_verified;
  const lastStatus = status?.latest_session?.status; // requires_input | processing | verified | canceled

  return (
    <div className="card-base mb-6" data-testid="security-identity-card">
      <div className="flex items-start gap-3">
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
            verified ? "bg-emerald-100 text-emerald-700" : "bg-neutral-100 text-neutral-500"
          }`}
        >
          <ShieldCheck className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-heading font-semibold text-lg text-agave">
            Verificación de identidad
          </h3>
          <p className="text-sm text-neutral-600 mt-1 max-w-[42ch]">
            Confirma tu identidad con una foto de tu INE / licencia y un selfie.
            Procesado por Stripe Identity, toma menos de 2 minutos.
          </p>

          {loading ? (
            <div className="mt-3 inline-flex items-center gap-2 text-sm text-neutral-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Cargando estado…
            </div>
          ) : verified ? (
            <div
              className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-800 text-xs font-medium ring-1 ring-emerald-200"
              data-testid="identity-verified-badge"
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              Verificada
            </div>
          ) : lastStatus === "processing" ? (
            <div
              className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 text-amber-800 text-xs font-medium ring-1 ring-amber-200"
              data-testid="identity-processing-badge"
            >
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Stripe está revisando tus documentos
            </div>
          ) : (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button
                onClick={handleStart}
                disabled={launching}
                data-testid="identity-start-btn"
                className="bg-terracotta hover:bg-terracotta/90"
              >
                {launching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                {lastStatus === "requires_input" ? "Reintentar verificación" : "Iniciar verificación"}
              </Button>
              {lastStatus === "requires_input" && status?.latest_session?.last_error ? (
                <span className="text-xs text-amber-700 inline-flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Stripe necesita una nueva foto
                </span>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
